import { useState, useEffect } from 'react';
import { useMutation, useLazyQuery, useQuery } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { CREAR_RESERVA } from '../../graphql/reservas';
import { GET_CLIENTES, CREAR_CLIENTE, CREAR_HUESPED, GET_HUESPED_POR_DOCUMENTO, ACTUALIZAR_HUESPED } from '../../graphql/huespedes';
import { GET_HABITACIONES } from '../../graphql/habitaciones';
import { GET_GRADOS_MILITARES } from '../../graphql/gradosMilitares';
import Modal from '../shared/Modal';
import Button from '../shared/Button';
import Input from '../shared/Input';
import Select from '../shared/Select';
import SuccessModal from '../shared/SuccessModal';
import { User, Search, X, Plus, DollarSign, FileText, MapPin, Award } from 'lucide-react';
import './ReservaModal.css';

function ReservaModal({ isOpen, onClose, selectionData = [], onSuccess }) {
  const navigate = useNavigate();

  // Estado del formulario
  const [formData, setFormData] = useState({
    // Información de reserva
    num_adultos: 1,
    num_ninos: 0,
    estado: 'pendiente',
    canal_reserva: 'directo',

    // Para reserva manual (sin calendario)
    habitacion_id_manual: '',
    fecha_entrada_manual: '',
    fecha_salida_manual: '',

    // Cliente/Huésped
    cliente_id: null,
    cliente: null,
    busqueda_cliente: '',

    // Nuevo cliente
    nuevo_cliente: false,
    cliente_nuevo: {
      tipo_documento: 'CC',
      numero_documento: '',
      nombre: '',
      apellido: '',
      email: '',
      telefono: '',
      direccion: '',
      ciudad: '',
      pais: 'Colombia',
      grado_militar_id: null
    },

    // Procedencia y destino
    procedencia: 'Colombia',
    destino: 'Colombia',
    motivo_viaje: '',

    // Tarifas y cálculos
    tipo_tarifa: 'mensual',
    anticipo: 0,

    // Notas
    observaciones: '',
    notas_especiales: ''
  });

  const [clientesSugeridos, setClientesSugeridos] = useState([]);
  const [mostrarSugerencias, setMostrarSugerencias] = useState(false);
  const [preciosPorHabitacion, setPreciosPorHabitacion] = useState({});
  const [successModal, setSuccessModal] = useState({ isOpen: false, message: '' });
  const [errorModal, setErrorModal] = useState({ isOpen: false, message: '' });
  const [gradoHuesped, setGradoHuesped] = useState(null); // Grado militar del huésped seleccionado

  // Modo manual: cuando no hay selectionData del calendario
  const modoManual = !selectionData || selectionData.length === 0;

  // Lazy query para búsqueda de clientes
  const [buscarClientes, { data: clientesData }] = useLazyQuery(GET_CLIENTES);

  // Lazy query para buscar huésped por documento
  const [buscarHuespedPorDocumento] = useLazyQuery(GET_HUESPED_POR_DOCUMENTO);

  // Query para obtener habitaciones (solo en modo manual)
  // No filtrar por estado — el backend valida disponibilidad real al crear la reserva
  const { data: habitacionesData } = useQuery(GET_HABITACIONES, {
    skip: !modoManual // Solo cargar si está en modo manual
  });

  // Query para obtener grados militares
  const { data: gradosData } = useQuery(GET_GRADOS_MILITARES, { variables: { activo: true } });
  const gradosMilitares = gradosData?.gradosMilitares || [];

  const habitacionesDisponibles = (habitacionesData?.habitaciones || []).filter(h => h.activa !== false && h.estado !== 'mantenimiento');

  // Mutations
  const [crearCliente, { loading: creandoCliente }] = useMutation(CREAR_CLIENTE);
  const [crearHuesped] = useMutation(CREAR_HUESPED);
  const [actualizarHuesped] = useMutation(ACTUALIZAR_HUESPED);
  const [crearReserva, { loading: creandoReserva }] = useMutation(CREAR_RESERVA, {
    onCompleted: (data) => {
      setSuccessModal({ isOpen: true, message: 'Reserva(s) creada(s) exitosamente' });
    },
    onError: (error) => {
      setErrorModal({ isOpen: true, message: `Error al crear reserva: ${error.message}` });
    }
  });

  // Precios dependen del grado militar del cliente, no de la habitación
  // Se inicializan en 0 hasta que se seleccione un cliente con grado
  useEffect(() => {
    if (selectionData && selectionData.length > 0 && !formData.cliente_id) {
      const precios = {};
      selectionData.forEach(reserva => {
        if (!precios[reserva.habitacion_id]) {
          precios[reserva.habitacion_id] = 0;
        }
      });
      setPreciosPorHabitacion(precios);
    }
  }, [selectionData]);

  // Buscar clientes cuando cambia el texto de búsqueda
  useEffect(() => {
    if (formData.busqueda_cliente.length >= 2) {
      buscarClientes({
        variables: { busqueda: formData.busqueda_cliente, activo: true }
      });
      setMostrarSugerencias(true);
    } else {
      setMostrarSugerencias(false);
    }
  }, [formData.busqueda_cliente, buscarClientes]);

  // Actualizar sugerencias cuando llegan datos
  useEffect(() => {
    if (clientesData?.clientes) {
      setClientesSugeridos(clientesData.clientes);
    }
  }, [clientesData]);

  // Calcular noches a partir de fechas
  const calcNochesManual = (entrada, salida) => {
    if (!entrada || !salida) return 0;
    const d1 = new Date(entrada + 'T12:00:00');
    const d2 = new Date(salida + 'T12:00:00');
    return Math.max(0, Math.round((d2 - d1) / (1000 * 60 * 60 * 24)));
  };

  // Calcular totales (precio viene del grado militar del cliente)
  // Para mensual: bloques de 30 días (ceil(noches/30) * precioMensual)
  // Para temporal: noches * precioNoche
  const calcularSubtotal = (precio, noches) => {
    if (formData.tipo_tarifa === 'mensual') {
      return Math.ceil(noches / 30) * precio;
    }
    return noches * precio;
  };

  const calcularTotales = () => {
    let subtotal = 0;

    if (modoManual) {
      // Modo manual: calcular desde datos del formulario
      const noches = calcNochesManual(formData.fecha_entrada_manual, formData.fecha_salida_manual);
      if (noches > 0 && gradoHuesped) {
        const precio = formData.tipo_tarifa === 'temporal'
          ? (parseFloat(gradoHuesped.precio_noche_temporal) || parseFloat(gradoHuesped.precio_noche))
          : parseFloat(gradoHuesped.precio_noche);
        subtotal = calcularSubtotal(precio, noches);
      }
    } else {
      selectionData.forEach(reserva => {
        const precio = preciosPorHabitacion[reserva.habitacion_id] || 0;
        subtotal += calcularSubtotal(precio, reserva.noches);
      });
    }

    const total = subtotal;
    const saldo = total - (parseFloat(formData.anticipo) || 0);

    return { subtotal, total, saldo };
  };

  const totales = calcularTotales();

  // Handlers
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // Si cambia tipo_tarifa, recalcular precios según grado
    if (name === 'tipo_tarifa' && gradoHuesped) {
      const precioGrado = value === 'temporal'
        ? (parseFloat(gradoHuesped.precio_noche_temporal) || parseFloat(gradoHuesped.precio_noche))
        : parseFloat(gradoHuesped.precio_noche);
      if (selectionData && selectionData.length > 0) {
        const nuevosPrecios = {};
        selectionData.forEach(reserva => {
          nuevosPrecios[reserva.habitacion_id] = precioGrado;
        });
        setPreciosPorHabitacion(nuevosPrecios);
      }
    }
  };

  const handleNuevoClienteChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      cliente_nuevo: {
        ...formData.cliente_nuevo,
        [name]: value
      }
    });
  };

  const handleSeleccionarCliente = async (cliente) => {
    setFormData({
      ...formData,
      cliente_id: cliente.id,
      cliente,
      busqueda_cliente: `${cliente.nombre} ${cliente.apellido || ''}`.trim(),
      nuevo_cliente: false
    });
    setMostrarSugerencias(false);

    // Tomar grado militar directamente del cliente
    if (cliente.grado_militar && (cliente.grado_militar.precio_noche > 0 || cliente.grado_militar.precio_noche_temporal > 0)) {
      const grado = cliente.grado_militar;
      setGradoHuesped(grado);
      // Llenar precios con tarifa del grado según tipo_tarifa actual
      const tipoTarifa = formData.tipo_tarifa || 'mensual';
      const precioGrado = tipoTarifa === 'temporal'
        ? (parseFloat(grado.precio_noche_temporal) || parseFloat(grado.precio_noche))
        : parseFloat(grado.precio_noche);
      if (selectionData && selectionData.length > 0) {
        const nuevosPrecios = {};
        selectionData.forEach(reserva => {
          nuevosPrecios[reserva.habitacion_id] = precioGrado;
        });
        setPreciosPorHabitacion(nuevosPrecios);
      }
    } else {
      setGradoHuesped(null);
    }
  };

  const handleLimpiarCliente = () => {
    setFormData({
      ...formData,
      cliente_id: null,
      cliente: null,
      busqueda_cliente: ''
    });
    setGradoHuesped(null);
    // Resetear precios a 0 al quitar cliente
    if (selectionData && selectionData.length > 0) {
      const precios = {};
      selectionData.forEach(reserva => {
        precios[reserva.habitacion_id] = 0;
      });
      setPreciosPorHabitacion(precios);
    }
  };


  const handleSubmit = async () => {
    // Validaciones
    if (!formData.cliente_id && !formData.nuevo_cliente) {
      setErrorModal({ isOpen: true, message: 'Por favor selecciona o crea un cliente' });
      return;
    }

    if (formData.nuevo_cliente && (!formData.cliente_nuevo.nombre || !formData.cliente_nuevo.numero_documento)) {
      setErrorModal({ isOpen: true, message: 'Por favor completa los datos del nuevo cliente' });
      return;
    }

    // Validar modo manual
    if (modoManual) {
      if (!formData.habitacion_id_manual || !formData.fecha_entrada_manual || !formData.fecha_salida_manual) {
        setErrorModal({ isOpen: true, message: 'Por favor completa todos los campos de la reserva' });
        return;
      }
    } else {
      if (selectionData.length === 0) {
        setErrorModal({ isOpen: true, message: 'No hay habitaciones seleccionadas' });
        return;
      }
    }

    try {
      let clienteId = formData.cliente_id ? parseInt(formData.cliente_id) : null;

      const numeroDocumento = formData.nuevo_cliente
        ? formData.cliente_nuevo.numero_documento
        : formData.cliente.numero_documento;
      const tipoDocumento = formData.nuevo_cliente
        ? formData.cliente_nuevo.tipo_documento
        : formData.cliente.tipo_documento;
      const nombreCliente = formData.nuevo_cliente
        ? formData.cliente_nuevo.nombre
        : formData.cliente.nombre;
      const apellidoCliente = formData.nuevo_cliente
        ? (formData.cliente_nuevo.apellido || '')
        : (formData.cliente.apellido || '');
      const gradoMilitarId = formData.nuevo_cliente
        ? (formData.cliente_nuevo.grado_militar_id ? parseInt(formData.cliente_nuevo.grado_militar_id) : null)
        : (formData.cliente?.grado_militar_id ? parseInt(formData.cliente.grado_militar_id) : null);

      // ========== PASO 1: Obtener o crear cliente ==========
      if (formData.nuevo_cliente) {
        const clienteInput = { ...formData.cliente_nuevo };
        if (clienteInput.grado_militar_id) {
          clienteInput.grado_militar_id = parseInt(clienteInput.grado_militar_id);
        } else {
          delete clienteInput.grado_militar_id;
        }

        try {
          const { data, errors } = await crearCliente({
            variables: { input: clienteInput }
          });
          if (data?.crearCliente?.id) {
            clienteId = parseInt(data.crearCliente.id);
          } else if (errors?.length > 0) {
            // Si ya existe, buscar por documento
            if (errors[0].message.includes('Ya existe')) {
              console.log('Cliente ya existe, buscando por documento...');
              const { data: clientesExistentes } = await buscarClientes({
                variables: { busqueda: numeroDocumento, activo: true }
              });
              const encontrado = clientesExistentes?.clientes?.find(
                c => c.numero_documento === numeroDocumento
              );
              if (encontrado) {
                clienteId = parseInt(encontrado.id);
              } else {
                throw new Error(errors[0].message);
              }
            } else {
              throw new Error(errors[0].message);
            }
          }
        } catch (clienteError) {
          // Error de red o excepción directa
          if (clienteError.message?.includes('Ya existe')) {
            const { data: clientesExistentes } = await buscarClientes({
              variables: { busqueda: numeroDocumento, activo: true }
            });
            const encontrado = clientesExistentes?.clientes?.find(
              c => c.numero_documento === numeroDocumento
            );
            if (encontrado) {
              clienteId = parseInt(encontrado.id);
            } else {
              throw clienteError;
            }
          } else {
            throw clienteError;
          }
        }
      }

      if (!clienteId) {
        throw new Error('No se pudo obtener el ID del cliente');
      }

      // ========== PASO 2: Obtener o crear huésped ==========
      let huespedId;

      // Primero buscar si ya existe el huésped
      try {
        const { data: huespedExistente } = await buscarHuespedPorDocumento({
          variables: { numero_documento: numeroDocumento }
        });
        if (huespedExistente?.huespedPorDocumento?.id) {
          huespedId = parseInt(huespedExistente.huespedPorDocumento.id);
          // Sincronizar grado_militar_id si el cliente tiene uno diferente
          const huespedGradoActual = huespedExistente.huespedPorDocumento.grado_militar_id
            ? parseInt(huespedExistente.huespedPorDocumento.grado_militar_id)
            : null;
          if (gradoMilitarId && gradoMilitarId !== huespedGradoActual) {
            try {
              await actualizarHuesped({
                variables: { id: huespedId, input: { grado_militar_id: gradoMilitarId } }
              });
            } catch (syncError) {
              console.warn('No se pudo sincronizar grado del huésped:', syncError.message);
            }
          }
        }
      } catch (busquedaError) {
        // No existe, se creará
      }

      // Si no existe, crear nuevo huésped
      if (!huespedId) {
        const huespedInput = {
          cliente_id: parseInt(clienteId),
          nombre: nombreCliente,
          apellido: apellidoCliente,
          tipo_documento: tipoDocumento,
          numero_documento: numeroDocumento,
          nacionalidad: formData.procedencia || 'Colombia',
          telefono: formData.nuevo_cliente ? formData.cliente_nuevo.telefono : formData.cliente.telefono,
          email: formData.nuevo_cliente ? formData.cliente_nuevo.email : formData.cliente.email,
          direccion: formData.nuevo_cliente ? formData.cliente_nuevo.direccion : formData.cliente.direccion,
          ciudad: formData.nuevo_cliente ? formData.cliente_nuevo.ciudad : formData.cliente.ciudad,
          pais: formData.nuevo_cliente ? formData.cliente_nuevo.pais : formData.cliente.pais,
          observaciones: formData.notas_especiales || null,
          preferencias: null,
          ...(gradoMilitarId ? { grado_militar_id: gradoMilitarId } : {})
        };

        try {
          const { data: huespedData, errors } = await crearHuesped({
            variables: { input: huespedInput }
          });
          if (huespedData?.crearHuesped?.id) {
            huespedId = parseInt(huespedData.crearHuesped.id);
          } else if (errors?.length > 0 && errors[0].message.includes('Ya existe')) {
            // Ya existe, buscar de nuevo
            const { data: h2 } = await buscarHuespedPorDocumento({
              variables: { numero_documento: numeroDocumento }
            });
            if (h2?.huespedPorDocumento?.id) {
              huespedId = parseInt(h2.huespedPorDocumento.id);
            }
          } else if (errors?.length > 0) {
            throw new Error(errors[0].message);
          }
        } catch (crearError) {
          if (crearError.message?.includes('Ya existe')) {
            const { data: h2 } = await buscarHuespedPorDocumento({
              variables: { numero_documento: numeroDocumento }
            });
            if (h2?.huespedPorDocumento?.id) {
              huespedId = parseInt(h2.huespedPorDocumento.id);
            } else {
              throw crearError;
            }
          } else {
            throw crearError;
          }
        }
      }

      if (!huespedId) {
        throw new Error('No se pudo obtener el ID del huésped');
      }

      // Modo manual: crear una sola reserva
      if (modoManual) {
        await crearReserva({
          variables: {
            input: {
              habitacion_id: parseInt(formData.habitacion_id_manual),
              huesped_id: parseInt(huespedId),
              fecha_entrada: formData.fecha_entrada_manual,
              fecha_salida: formData.fecha_salida_manual,
              num_adultos: 1,
              num_ninos: 0,
              canal: formData.canal_reserva,
              observaciones: `${formData.observaciones}\nProcedencia: ${formData.procedencia}\nDestino: ${formData.destino}\nMotivo: ${formData.motivo_viaje}`.trim(),
              anticipo: parseFloat(formData.anticipo) || 0,
              tipo_tarifa: formData.tipo_tarifa || 'mensual',
              estado: formData.estado || 'pendiente'
            }
          }
        });
      } else {
        // Modo calendario: crear múltiples reservas
        const anticipoTotal = parseFloat(formData.anticipo) || 0;
        const totalSubtotal = selectionData.reduce((sum, reserva) => {
          const precio = preciosPorHabitacion[reserva.habitacion_id] || 0;
          return sum + calcularSubtotal(precio, reserva.noches);
        }, 0);

        // Crear reservas para cada habitación seleccionada
        const promesas = selectionData.map(reserva => {
          const precio = preciosPorHabitacion[reserva.habitacion_id] || 0;
          const subtotalHabitacion = calcularSubtotal(precio, reserva.noches);

          // Anticipo proporcional
          const anticipoProporcional = totalSubtotal > 0
            ? (subtotalHabitacion / totalSubtotal) * anticipoTotal
            : 0;

          return crearReserva({
            variables: {
              input: {
                habitacion_id: reserva.habitacion_id,
                huesped_id: parseInt(huespedId),
                fecha_entrada: reserva.fecha_entrada,
                fecha_salida: reserva.fecha_salida,
                num_adultos: parseInt(formData.num_adultos) || 1,
                num_ninos: parseInt(formData.num_ninos) || 0,
                canal: formData.canal_reserva,
                observaciones: `${formData.observaciones}\nProcedencia: ${formData.procedencia}\nDestino: ${formData.destino}\nMotivo: ${formData.motivo_viaje}`.trim(),
                anticipo: Math.round(anticipoProporcional),
                tipo_tarifa: formData.tipo_tarifa || 'mensual',
                estado: formData.estado || 'pendiente'
              }
            }
          });
        });

        await Promise.all(promesas);
      }
    } catch (error) {
      console.error('Error al crear reserva(s):', error);
      setErrorModal({ isOpen: true, message: `Error al crear reserva(s): ${error.message}` });
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0
    }).format(price);
  };

  const formatDate = (dateString) => {
    // Parsear YYYY-MM-DD sin timezone shift
    const [y, m, d] = dateString.split('T')[0].split('-').map(Number);
    const fecha = new Date(y, m - 1, d);
    return fecha.toLocaleDateString('es-CO', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={modoManual ? 'Nueva Reserva Manual' : `Nueva Reserva - ${selectionData.length} ${selectionData.length === 1 ? 'Habitación' : 'Habitaciones'}`}
      size="large"
    >
      <div className="reserva-modal-nuevo">
        {/* BLOQUE 1: Información de Reserva */}
        <div className="bloque-reserva">
          <h3 className="bloque-title">
            <FileText size={18} />
            Información de Reserva
          </h3>

          {/* Modo Manual: Seleccionar habitación y fechas */}
          {modoManual ? (
            <div className="form-row">
              <Select
                label="Habitación *"
                name="habitacion_id_manual"
                value={formData.habitacion_id_manual}
                onChange={handleInputChange}
                options={[
                  { value: '', label: 'Seleccionar habitación...' },
                  ...habitacionesDisponibles.map(hab => ({
                    value: hab.id.toString(),
                    label: `Hab. ${hab.numero} - ${hab.tipo} | ${hab.capacidad} cama(s)`
                  }))
                ]}
                required
              />
              <Input
                type="date"
                label="Fecha Entrada *"
                name="fecha_entrada_manual"
                value={formData.fecha_entrada_manual}
                onChange={handleInputChange}
                required
              />
              <Input
                type="date"
                label="Fecha Salida *"
                name="fecha_salida_manual"
                value={formData.fecha_salida_manual}
                onChange={handleInputChange}
                required
              />
            </div>
          ) : (
            /* Modo Calendario: Mostrar selección */
            <div className="habitaciones-seleccionadas">
              {selectionData.map((reserva, index) => (
                <div key={index} className="habitacion-item">
                  <div className="hab-info">
                    <span className="hab-numero">Hab. {reserva.habitacion.numero}</span>
                    <span className="hab-tipo">{reserva.habitacion.tipo}</span>
                  </div>
                  <div className="hab-fechas">
                    <span>{formatDate(reserva.fecha_entrada)} → {formatDate(reserva.fecha_salida)}</span>
                    <span className="hab-noches">({reserva.noches} {reserva.noches === 1 ? 'noche' : 'noches'})</span>
                  </div>
                  <div className="hab-precio">
                    <span className="calculo-value">
                      {formatPrice(preciosPorHabitacion[reserva.habitacion_id] || 0)}
                    </span>
                    <span className="precio-label">/{formData.tipo_tarifa === 'mensual' ? 'mes' : 'noche'}</span>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="form-row">
            <Select
              label="Estado"
              name="estado"
              value={formData.estado}
              onChange={handleInputChange}
              options={[
                { value: 'pendiente', label: 'Pendiente' },
                { value: 'confirmada', label: 'Confirmada' }
              ]}
            />

            <Select
              label="Canal"
              name="canal_reserva"
              value={formData.canal_reserva}
              onChange={handleInputChange}
              options={[
                { value: 'directo', label: 'Directo' },
                { value: 'telefono', label: 'Teléfono' },
                { value: 'web', label: 'Web' },
                { value: 'walk_in', label: 'Walk-in' }
              ]}
            />
          </div>
        </div>

        {/* BLOQUE 2: Información del Titular */}
        <div className="bloque-titular">
          <h3 className="bloque-title">
            <User size={18} />
            Información del Titular
          </h3>

          <div className="cliente-toggle">
            <Button
              variant={!formData.nuevo_cliente ? 'primary' : 'outline'}
              size="sm"
              onClick={() => setFormData({ ...formData, nuevo_cliente: false })}
            >
              Cliente Existente
            </Button>
            <Button
              variant={formData.nuevo_cliente ? 'primary' : 'outline'}
              size="sm"
              onClick={() => setFormData({ ...formData, nuevo_cliente: true, cliente_id: null, cliente: null })}
            >
              <Plus size={16} />
              Nuevo Cliente
            </Button>
          </div>

          {!formData.nuevo_cliente ? (
            <div className="busqueda-cliente">
              <div className="busqueda-input-wrapper">
                <Search className="busqueda-icon" size={18} />
                <input
                  type="text"
                  className="busqueda-input"
                  placeholder="Buscar por nombre, documento, teléfono o email..."
                  value={formData.busqueda_cliente}
                  onChange={(e) => setFormData({ ...formData, busqueda_cliente: e.target.value })}
                  onFocus={() => formData.busqueda_cliente.length >= 2 && setMostrarSugerencias(true)}
                />
                {formData.cliente_id && (
                  <button className="busqueda-clear" onClick={handleLimpiarCliente}>
                    <X size={18} />
                  </button>
                )}
              </div>

              {mostrarSugerencias && clientesSugeridos.length > 0 && (
                <div className="sugerencias-dropdown">
                  {clientesSugeridos.map(cliente => (
                    <div
                      key={cliente.id}
                      className="sugerencia-item"
                      onClick={() => handleSeleccionarCliente(cliente)}
                    >
                      <div className="sugerencia-nombre">
                        {cliente.nombre} {cliente.apellido}
                      </div>
                      <div className="sugerencia-detalles">
                        <span>{cliente.tipo_documento}: {cliente.numero_documento}</span>
                        {cliente.telefono && <span>Tel: {cliente.telefono}</span>}
                        {cliente.email && <span>{cliente.email}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {formData.cliente && (
                <div className="cliente-seleccionado">
                  <div className="cliente-sel-header">
                    <strong>{formData.cliente.nombre} {formData.cliente.apellido}</strong>
                    <span className="cliente-sel-doc">{formData.cliente.tipo_documento}: {formData.cliente.numero_documento}</span>
                  </div>
                  {formData.cliente.telefono && <div>Tel: {formData.cliente.telefono}</div>}
                  {formData.cliente.email && <div>Email: {formData.cliente.email}</div>}
                  {formData.cliente.direccion && <div>Dir: {formData.cliente.direccion}</div>}
                </div>
              )}
            </div>
          ) : (
            <div className="form-nuevo-cliente">
              <div className="form-row">
                <Select
                  label="Tipo de Documento"
                  name="tipo_documento"
                  value={formData.cliente_nuevo.tipo_documento}
                  onChange={handleNuevoClienteChange}
                  options={[
                    { value: 'CC', label: 'Cédula de Ciudadanía' },
                    { value: 'CE', label: 'Cédula de Extranjería' },
                    { value: 'Pasaporte', label: 'Pasaporte' },
                    { value: 'TI', label: 'Tarjeta de Identidad' },
                    { value: 'NIT', label: 'NIT' }
                  ]}
                />

                <Input
                  label="Número de Documento *"
                  name="numero_documento"
                  value={formData.cliente_nuevo.numero_documento}
                  onChange={handleNuevoClienteChange}
                  required
                />
              </div>

              <div className="form-row">
                <Input
                  label="Nombre *"
                  name="nombre"
                  value={formData.cliente_nuevo.nombre}
                  onChange={handleNuevoClienteChange}
                  required
                />

                <Input
                  label="Apellido"
                  name="apellido"
                  value={formData.cliente_nuevo.apellido}
                  onChange={handleNuevoClienteChange}
                />
              </div>

              <div className="form-row">
                <Input
                  type="email"
                  label="Email"
                  name="email"
                  value={formData.cliente_nuevo.email}
                  onChange={handleNuevoClienteChange}
                />

                <Input
                  type="tel"
                  label="Teléfono"
                  name="telefono"
                  value={formData.cliente_nuevo.telefono}
                  onChange={handleNuevoClienteChange}
                />
              </div>

              <Input
                label="Dirección"
                name="direccion"
                value={formData.cliente_nuevo.direccion}
                onChange={handleNuevoClienteChange}
              />

              <div className="form-row">
                <Input
                  label="Ciudad"
                  name="ciudad"
                  value={formData.cliente_nuevo.ciudad}
                  onChange={handleNuevoClienteChange}
                />

                <Input
                  label="País"
                  name="pais"
                  value={formData.cliente_nuevo.pais}
                  onChange={handleNuevoClienteChange}
                />
              </div>

              {/* Selector de Grado Militar */}
              <div className="form-group" style={{marginBottom: '1rem'}}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem', fontWeight: 500 }}>
                  <Award size={16} />
                  Grado Militar
                </label>
                <select
                  value={formData.cliente_nuevo.grado_militar_id || ''}
                  onChange={(e) => {
                    const gradoId = e.target.value ? parseInt(e.target.value) : null;
                    setFormData(prev => ({
                      ...prev,
                      cliente_nuevo: { ...prev.cliente_nuevo, grado_militar_id: gradoId }
                    }));
                    // Actualizar gradoHuesped para mostrar selector de tarifa
                    if (gradoId) {
                      const grado = gradosMilitares.find(g => parseInt(g.id) === gradoId);
                      if (grado) {
                        setGradoHuesped(grado);
                        const tipoTarifa = formData.tipo_tarifa || 'mensual';
                        const precioGrado = tipoTarifa === 'temporal'
                          ? (parseFloat(grado.precio_noche_temporal) || parseFloat(grado.precio_noche))
                          : parseFloat(grado.precio_noche);
                        if (selectionData && selectionData.length > 0) {
                          const nuevosPrecios = {};
                          selectionData.forEach(reserva => {
                            nuevosPrecios[reserva.habitacion_id] = precioGrado;
                          });
                          setPreciosPorHabitacion(nuevosPrecios);
                        }
                      }
                    } else {
                      setGradoHuesped(null);
                      if (selectionData && selectionData.length > 0) {
                        const precios = {};
                        selectionData.forEach(reserva => {
                          precios[reserva.habitacion_id] = 0;
                        });
                        setPreciosPorHabitacion(precios);
                      }
                    }
                  }}
                  style={{width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px'}}
                >
                  <option value="">Civil (sin grado)</option>
                  {gradosMilitares.map(g => (
                    <option key={g.id} value={g.id}>
                      {g.nombre} - Mensual: ${parseInt(g.precio_noche).toLocaleString()} | Temporal: ${parseInt(g.precio_noche_temporal || 0).toLocaleString()}
                    </option>
                  ))}
                </select>
              </div>

            </div>
          )}
        </div>

        {/* Info Grado Militar + Tipo Tarifa */}
        {gradoHuesped && (
          <div className="bloque-grado-militar" style={{
            padding: '0.75rem 1rem',
            background: 'var(--hotel-info-bg, #eff6ff)',
            border: '1px solid var(--hotel-info-border, #bfdbfe)',
            borderRadius: '8px',
            marginBottom: '0'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
              <Award size={20} style={{ color: 'var(--hotel-primary, #2563eb)', flexShrink: 0 }} />
              <div>
                <strong>Grado: {gradoHuesped.nombre}</strong>
                <span style={{ marginLeft: '0.5rem', fontSize: '0.85rem', color: 'var(--hotel-text-secondary)' }}>
                  Mensual: {formatPrice(gradoHuesped.precio_noche)} | Temporal: {formatPrice(gradoHuesped.precio_noche_temporal || 0)}
                </span>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
              <span style={{ fontSize: '0.85rem', fontWeight: '600' }}>Tipo de Tarifa:</span>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', cursor: 'pointer', fontSize: '0.85rem' }}>
                <input
                  type="radio"
                  name="tipo_tarifa"
                  value="mensual"
                  checked={formData.tipo_tarifa === 'mensual'}
                  onChange={handleInputChange}
                />
                Mensual ({formatPrice(gradoHuesped.precio_noche)}/mes)
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', cursor: 'pointer', fontSize: '0.85rem' }}>
                <input
                  type="radio"
                  name="tipo_tarifa"
                  value="temporal"
                  checked={formData.tipo_tarifa === 'temporal'}
                  onChange={handleInputChange}
                />
                Temporal ({formatPrice(gradoHuesped.precio_noche_temporal || 0)}/noche)
              </label>
            </div>
          </div>
        )}

        {/* BLOQUE 3: Procedencia y Destino */}
        <div className="bloque-procedencia">
          <h3 className="bloque-title">
            <MapPin size={18} />
            Procedencia y Destino
          </h3>

          <div className="form-row">
            <Input
              label="Procedencia"
              name="procedencia"
              value={formData.procedencia}
              onChange={handleInputChange}
              placeholder="Colombia"
            />

            <Input
              label="Destino"
              name="destino"
              value={formData.destino}
              onChange={handleInputChange}
              placeholder="Colombia"
            />

            <Input
              label="Motivo de Viaje"
              name="motivo_viaje"
              value={formData.motivo_viaje}
              onChange={handleInputChange}
              placeholder="Turismo, Negocios, etc."
            />
          </div>
        </div>

        {/* BLOQUE 4: Tarifas */}
        <div className="bloque-tarifas">
          <h3 className="bloque-title">
            <DollarSign size={18} />
            Tarifas
          </h3>

          <div className="calculo-grid">
            <div className="calculo-item total">
              <span className="calculo-label">Total:</span>
              <span className="calculo-value">{formatPrice(totales.total)}</span>
            </div>

            <Input
              type="number"
              label="Anticipo"
              name="anticipo"
              value={formData.anticipo}
              onChange={handleInputChange}
              min="0"
              max={totales.total}
            />

            <div className="calculo-item saldo">
              <span className="calculo-label">Saldo Pendiente:</span>
              <span className="calculo-value">{formatPrice(totales.saldo)}</span>
            </div>
          </div>
        </div>

        {/* BLOQUE 5: Notas */}
        <div className="bloque-notas">
          <div className="form-row">
            <div className="input-group">
              <label className="input-label">Observaciones</label>
              <textarea
                name="observaciones"
                value={formData.observaciones}
                onChange={handleInputChange}
                rows="3"
                className="textarea"
                placeholder="Observaciones generales..."
              />
            </div>

            <div className="input-group">
              <label className="input-label">Notas Especiales</label>
              <textarea
                name="notas_especiales"
                value={formData.notas_especiales}
                onChange={handleInputChange}
                rows="3"
                className="textarea"
                placeholder="Preferencias del huésped, solicitudes especiales..."
              />
            </div>
          </div>
        </div>

        {/* Footer con botones */}
        <div className="modal-footer">
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={creandoReserva || creandoCliente}
          >
            {creandoReserva || creandoCliente ? 'Creando...' : (modoManual ? 'Crear Reserva' : `Crear ${selectionData.length} Reserva${selectionData.length > 1 ? 's' : ''}`)}
          </Button>
        </div>
      </div>

      {/* Modales de éxito y error */}
      <SuccessModal
        isOpen={successModal.isOpen}
        onClose={() => {
          setSuccessModal({ isOpen: false, message: '' });
          if (onSuccess) {
            onSuccess();
          } else {
            onClose();
            window.location.reload();
          }
        }}
        type="success"
        message={successModal.message}
      />

      <SuccessModal
        isOpen={errorModal.isOpen}
        onClose={() => setErrorModal({ isOpen: false, message: '' })}
        type="error"
        message={errorModal.message}
      />
    </Modal>
  );
}

export default ReservaModal;